/**
 * @file: RecentChats.tsx
 *
 * The empty-query default state of chat search: the five most-recent threads
 * as quick-pick rows (or a hint when there are none). Picking one emits a
 * `ChatSearchPick` to the host.
 */

import { Typography } from '@euler/app/components/typography';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useChatThreads } from '@euler/app/flows/chat/lib/threads/context';
import { ChatSearchPick } from '@euler/app/flows/chat/lib/types';
import {
  chatThreadAvatar,
  chatThreadTitle,
} from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const RecentChats: FC<{ onPick: (pick: ChatSearchPick) => void }> = memo(
  ({ onPick }) => {
    const { rpx } = useResponsiveContext();
    const { chatThreads$ } = useChatThreads();
    const { me } = useChatSession();
    const selfName = me.user.name;
    const [threads] = useBehaviorSubject(chatThreads$);

    const recentThreads = useMemo(() => threads.slice(0, 5), [threads]);

    if (recentThreads.length === 0) {
      return (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: rpx(40),
          }}
        >
          <Typography.Text
            size={rpx(14)}
            color="#9ca3af"
            style={{ textAlign: 'center' }}
          >
            输入关键字搜索联系人、群聊或消息内容
          </Typography.Text>
        </View>
      );
    }

    return (
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        <View
          style={{
            paddingHorizontal: rpx(16),
            paddingTop: rpx(16),
            paddingBottom: rpx(6),
          }}
        >
          <Typography.Text
            size={rpx(12)}
            color="#6b7280"
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            最近聊天
          </Typography.Text>
        </View>
        {recentThreads.map(t => {
          const displayName = chatThreadTitle(t, selfName);
          return (
            <TouchableOpacity
              key={t.id}
              onPress={() => onPick({ kind: 'thread', thread: t, displayName })}
              activeOpacity={0.6}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: rpx(12),
                paddingHorizontal: rpx(16),
                backgroundColor: '#fff',
              }}
            >
              <Avatar
                {...chatThreadAvatar(t)}
                textStyle={{
                  fontFamily: FontFamily.NotoSans.Medium,
                  fontSize: rpx(14),
                  lineHeight: rpx(18),
                }}
                style={{
                  width: rpx(36),
                  height: rpx(36),
                  borderRadius: rpx(18),
                  marginRight: rpx(10),
                }}
              />
              <Typography.Text size={rpx(14)} color="#111">
                {displayName}
              </Typography.Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  },
);
